import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BrowserBadge, BROWSER_COLORS, Button, EmptyState, Favicon, Icon, Modal, Skeleton } from '@vault/ui';
import type { BrowserName } from '@vault/ui';
import type { Browser } from '@vault/shared';
import { api } from '../api/client.js';
import type { TrashEntry } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { browserLabel, errMessage, hashIndex, hostname, initial, useToasts } from './_vault/shared.js';
import { daysUntilPurge, deletedAgo, entryKey, entryTitle, purgeLabel, purgeTone } from './trash/helpers.js';

/**
 * Trash — 14-day recovery bin for soft-deleted sweeps and pages.
 * Lives under Settings (app.tabs.nomadnet.ai/settings/trash): restore or
 * delete-forever per entry, restore a selection, or empty the whole bin.
 * Ported from `design-handoff/.../Vault Trash.dc.html`.
 */
export function TrashPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toasts = useToasts();
  const { t, plural } = useT();

  const trashQ = useQuery({ queryKey: ['trash'], queryFn: () => api.trash.list() });
  const entries = useMemo<TrashEntry[]>(() => trashQ.data?.entries ?? [], [trashQ.data]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingPurge, setPendingPurge] = useState<TrashEntry | null>(null);
  const [emptyOpen, setEmptyOpen] = useState(false);

  const selectedEntries = useMemo(
    () => entries.filter((e) => selected.has(entryKey(e))),
    [entries, selected],
  );

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['trash'] });
    void qc.invalidateQueries({ queryKey: ['sweeps'] });
    void qc.invalidateQueries({ queryKey: ['usage'] });
  }

  const restoreMut = useMutation({
    mutationFn: (e: TrashEntry) => api.trash.restore(e.kind, e.id),
    onSuccess: (_res, e) => {
      invalidate();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(entryKey(e));
        return next;
      });
      toasts.show({ tone: 'success', title: t('trash.toast.restored'), description: t('trash.toast.restoredBody', { title: entryTitle(e) }) });
    },
    onError: (err) => toasts.show({ tone: 'danger', title: t('trash.toast.restoreFailed'), description: errMessage(err) }),
  });

  const restoreManyMut = useMutation({
    mutationFn: async (list: TrashEntry[]) => {
      await Promise.all(list.map((e) => api.trash.restore(e.kind, e.id)));
      return list.length;
    },
    onSuccess: (count) => {
      invalidate();
      setSelected(new Set());
      toasts.show({ tone: 'success', title: t('trash.toast.restoredMany', { entries: plural('unit.entries', count) }) });
    },
    onError: (err) => {
      invalidate(); // partial success is possible — refetch so the list stays honest
      toasts.show({ tone: 'danger', title: t('trash.toast.restoreManyFailed'), description: errMessage(err) });
    },
  });

  const purgeMut = useMutation({
    mutationFn: (e: TrashEntry) => api.trash.purge(e.kind, e.id),
    onSuccess: (_res, e) => {
      invalidate();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(entryKey(e));
        return next;
      });
      setPendingPurge(null);
      toasts.show({ tone: 'warn', title: t('trash.toast.purged'), description: t('trash.toast.purgedBody', { title: entryTitle(e) }) });
    },
    onError: (err) => {
      setPendingPurge(null);
      toasts.show({ tone: 'danger', title: t('trash.toast.purgeFailed'), description: errMessage(err) });
    },
  });

  const emptyMut = useMutation({
    mutationFn: () => api.trash.empty(),
    onSuccess: () => {
      invalidate();
      setSelected(new Set());
      setEmptyOpen(false);
      toasts.show({ tone: 'warn', title: t('trash.toast.emptied'), description: t('trash.toast.emptiedBody') });
    },
    onError: (err) => {
      setEmptyOpen(false);
      toasts.show({ tone: 'danger', title: t('trash.toast.emptyFailed'), description: errMessage(err) });
    },
  });

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const loaded = !trashQ.isPending && !trashQ.isError;

  return (
    <div style={page}>
      <style>{TRASH_CSS}</style>

      {/* ── header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="tvt-back" onClick={() => navigate('/settings')}>
          <Icon name="chevron-left" size={13} color="currentColor" />
          {t('nav.settings')}
        </button>
        <h1 style={heading}>{t('trash.title')}</h1>
        <span style={metaPill}>
          <span>{trashQ.isPending ? t('trash.itemsLoading') : plural('unit.items', entries.length).toUpperCase()}</span>
          <span style={pillDot} />
          <span>{t('trash.autoPurge')}</span>
        </span>
        <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 8 }}>
          {selectedEntries.length > 0 && (
            <button
              type="button"
              className="tvt-pill ghost"
              disabled={restoreManyMut.isPending}
              onClick={() => restoreManyMut.mutate(selectedEntries)}
            >
              {restoreManyMut.isPending ? t('trash.restoring') : t('trash.restoreSelected', { count: selectedEntries.length })}
            </button>
          )}
          <button
            type="button"
            className="tvt-pill danger"
            disabled={entries.length === 0 || emptyMut.isPending}
            onClick={() => setEmptyOpen(true)}
          >
            <Icon name="trash" size={12} color="currentColor" />
            {t('trash.emptyTrash')}
          </button>
        </span>
      </div>

      {/* ── list states ── */}
      {trashQ.isPending && <ListSkeleton />}

      {trashQ.isError && (
        <EmptyState
          icon={<Icon name="clock" size={18} color="var(--amber-ink)" />}
          title={t('trash.error.title')}
          description={errMessage(trashQ.error)}
          action={
            <Button variant="secondary" size="sm" onClick={() => void trashQ.refetch()}>
              {t('common.tryAgain')}
            </Button>
          }
        />
      )}

      {loaded && entries.length === 0 && (
        <EmptyState
          icon={<Icon name="trash" size={18} color="var(--text-3)" />}
          title={t('trash.emptyState.title')}
          description={t('trash.emptyState.body')}
        />
      )}

      {loaded && entries.length > 0 && (
        <div style={listShell}>
          {entries.map((e, i) => (
            <TrashRow
              key={entryKey(e)}
              entry={e}
              last={i === entries.length - 1}
              checked={selected.has(entryKey(e))}
              onToggle={() => toggle(entryKey(e))}
              restoring={restoreMut.isPending && restoreMut.variables?.id === e.id && restoreMut.variables.kind === e.kind}
              onRestore={() => restoreMut.mutate(e)}
              onPurge={() => setPendingPurge(e)}
            />
          ))}
        </div>
      )}

      <span style={footNote}>
        {t('trash.footNote')}
      </span>

      {/* ── delete-forever confirm ── */}
      {pendingPurge && (
        <Modal
          title={t('trash.purge.title')}
          subtitle={t('trash.purge.subtitle', { title: entryTitle(pendingPurge) })}
          width={420}
          onClose={() => setPendingPurge(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingPurge(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" loading={purgeMut.isPending} onClick={() => purgeMut.mutate(pendingPurge)}>
                {t('trash.purge.confirm')}
              </Button>
            </>
          }
        >
          <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
            {pendingPurge.kind === 'sweep'
              ? t('trash.purge.sweepBody', { tabs: plural('unit.tabs', pendingPurge.tabCount ?? 0) })
              : t('trash.purge.pageBody')}
          </span>
        </Modal>
      )}

      {/* ── empty-trash confirm ── */}
      {emptyOpen && (
        <Modal
          title={t('trash.emptyConfirm.title')}
          subtitle={t('trash.emptyConfirm.subtitle', { entries: plural('unit.entries', entries.length) })}
          width={420}
          onClose={() => setEmptyOpen(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setEmptyOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" loading={emptyMut.isPending} onClick={() => emptyMut.mutate()}>
                {t('trash.emptyTrash')}
              </Button>
            </>
          }
        >
          <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
            {t('trash.emptyConfirm.body')}
          </span>
        </Modal>
      )}

      {toasts.node}
    </div>
  );
}

// ── row ─────────────────────────────────────────────────────────────────────

function TrashRow({
  entry,
  last,
  checked,
  onToggle,
  restoring,
  onRestore,
  onPurge,
}: {
  entry: TrashEntry;
  last: boolean;
  checked: boolean;
  onToggle: () => void;
  restoring: boolean;
  onRestore: () => void;
  onPurge: () => void;
}) {
  const { t } = useT();
  const title = entryTitle(entry);
  const days = daysUntilPurge(entry.purgeAt);
  const tone = purgeTone(days);
  const toneColor = tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--amber-ink)' : 'var(--text-3)';

  const meta =
    entry.kind === 'sweep'
      ? `SESSION · ${entry.tabCount ?? 0} ${(entry.tabCount ?? 0) === 1 ? 'TAB' : 'TABS'} · ${deletedAgo(entry.deletedAt)}`
      : `PAGE · ${entry.url ? hostname(entry.url) : '—'} · ${deletedAgo(entry.deletedAt)}`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 16px',
        borderBottom: last ? 'none' : '1px solid var(--line)',
        background: checked ? 'var(--primary-tint)' : 'transparent',
      }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={checked ? `Deselect ${title}` : `Select ${title}`}
        className="tvt-check"
        onClick={onToggle}
        style={
          checked
            ? { background: 'var(--primary)', border: 'none' }
            : { background: 'transparent', border: '1.5px solid var(--line-strong)' }
        }
      >
        {checked && <Icon name="check" size={9} color="var(--on-primary)" />}
      </button>

      <EntryFavicon entry={entry} />

      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {meta}
        </span>
      </span>

      <EntryBrowserBadge browser={entry.browser} />

      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: 'var(--font-mono)',
          fontSize: 9.5,
          color: toneColor,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {tone === 'danger' && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--danger)' }} />}
        {purgeLabel(days)}
      </span>

      <button type="button" className="tvt-pill primary" disabled={restoring} onClick={onRestore}>
        {restoring ? t('trash.restoring') : t('common.restore')}
      </button>

      <button type="button" className="tvt-icon" title={t('trash.purge.confirm')} aria-label={`${t('trash.purge.confirm')}: ${title}`} onClick={onPurge}>
        <Icon name="trash" size={12} />
      </button>
    </div>
  );
}

// ── row fragments ───────────────────────────────────────────────────────────

/** Single site-initial favicon; sweeps stack a "+N remaining tabs" chip. */
function EntryFavicon({ entry }: { entry: TrashEntry }) {
  const seed = entry.title ?? entry.url ?? entry.id;
  const idx = hashIndex(seed, 8);
  const letter = initial(seed);
  const rest = entry.kind === 'sweep' ? Math.max(0, (entry.tabCount ?? 1) - 1) : 0;

  if (rest === 0) return <Favicon letter={letter} index={idx} size={20} />;
  return (
    <span style={{ display: 'inline-flex', flexShrink: 0 }}>
      <Favicon letter={letter} index={idx} size={20} />
      <span style={plusChip}>+{rest}</span>
    </span>
  );
}

const KNOWN_BROWSERS = Object.keys(BROWSER_COLORS) as BrowserName[];

function EntryBrowserBadge({ browser }: { browser: string | null }) {
  if (!browser || browser === 'unknown') return null;
  const known = KNOWN_BROWSERS.find((n) => n === browser);
  if (known) return <BrowserBadge browser={known} size="sm" />;
  // e.g. "chromium" — chrome-family dot with an honest label
  return <BrowserBadge browser="chrome" label={browserLabel(browser as Browser)} size="sm" />;
}

// ── loading skeleton ────────────────────────────────────────────────────────

function ListSkeleton() {
  return (
    <div style={listShell}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderBottom: i === 3 ? 'none' : '1px solid var(--line)' }}
        >
          <Skeleton width={16} height={16} radius={5} />
          <Skeleton width={20} height={20} radius={6} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <Skeleton width="34%" height={13} />
            <Skeleton width={200} height={9} />
          </div>
          <Skeleton width={96} height={11} />
          <Skeleton width={82} height={28} radius="var(--r-pill)" />
        </div>
      ))}
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────

const page: CSSProperties = {
  maxWidth: 1120,
  margin: '0 auto',
  padding: '24px 28px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const heading: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em',
};
const metaPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 14,
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '7px 16px',
  fontFamily: 'var(--font-mono)',
  fontSize: 10.5,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};
const pillDot: CSSProperties = { width: 4, height: 4, borderRadius: '50%', background: 'var(--line-strong)' };
const listShell: CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 14,
  overflow: 'hidden',
};
const plusChip: CSSProperties = {
  marginLeft: -7,
  width: 20,
  height: 20,
  borderRadius: 6,
  background: 'var(--surface-3)',
  border: '2px solid var(--surface-1)',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-body)',
  fontSize: 8,
  fontWeight: 600,
  color: 'var(--text-2)',
};
const footNote: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--text-3)',
  textAlign: 'center',
  marginTop: 8,
};

const TRASH_CSS = `
.tvt-back{display:inline-flex;align-items:center;gap:7px;font-family:var(--font-body);font-size:12.5px;font-weight:600;
  color:var(--text-2);background:none;border:none;padding:0;cursor:pointer}
.tvt-back:hover{color:var(--text-1)}
.tvt-pill{display:inline-flex;align-items:center;gap:8px;border-radius:var(--r-pill,999px);padding:9px 16px;
  font-family:var(--font-body);font-size:12.5px;font-weight:600;background:transparent;cursor:pointer;white-space:nowrap}
.tvt-pill:disabled{opacity:.45;cursor:not-allowed}
.tvt-pill.ghost{border:1px solid var(--line-strong);color:var(--text-2)}
.tvt-pill.ghost:hover:not(:disabled){background:var(--surface-1)}
.tvt-pill.danger{border:1px solid var(--danger-line);color:var(--danger)}
.tvt-pill.danger:hover:not(:disabled){background:var(--danger-tint)}
.tvt-pill.primary{border:1px solid var(--line-primary);color:var(--primary);padding:6px 14px;font-size:12px;flex-shrink:0}
.tvt-pill.primary:hover:not(:disabled){background:var(--primary-tint)}
.tvt-check{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:5px;
  flex-shrink:0;cursor:pointer;padding:0;box-sizing:border-box}
.tvt-icon{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:7px;
  border:1px solid var(--line-strong);background:transparent;color:var(--text-2);cursor:pointer;flex-shrink:0}
.tvt-icon:hover{background:var(--danger-tint);color:var(--danger);border-color:var(--danger-line)}
@media (max-width:720px){.tvt-pill.ghost,.tvt-pill.danger{padding:7px 12px;font-size:12px}}
`;
